import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { FaSearch } from 'react-icons/fa';

const CTRL_BTN_CLASS =
	'tw:flex tw:h-[34px] tw:w-[34px] tw:items-center tw:justify-center tw:rounded-token tw:border tw:border-border-strong tw:bg-surface tw:text-[14px] tw:text-ink-1 tw:transition-colors tw:duration-100 tw:hover:border-ink-3 tw:hover:bg-surface-2 tw:disabled:cursor-not-allowed tw:disabled:opacity-35 tw:disabled:hover:border-border-strong tw:disabled:hover:bg-surface';

function CtrlButton({ label, onClick, disabled, children }) {
	return (
		<button
			type='button'
			className={CTRL_BTN_CLASS}
			onClick={onClick}
			disabled={disabled}
			aria-label={label}
			title={label}
		>
			{children}
		</button>
	);
}

function Box({ heading, query, onQueryChange, searchPlaceholder, items, checked, onToggle, disabled, emptyMessage }) {
	return (
		<div className='tw:flex tw:h-[320px] tw:flex-col tw:overflow-hidden tw:rounded-token tw:border tw:border-border tw:bg-surface'>
			<div className='tw:flex tw:items-center tw:gap-2 tw:border-b tw:border-border tw:px-3 tw:py-2'>
				<FaSearch className='tw:text-[12px] tw:text-ink-3' />
				<input
					type='text'
					value={query}
					onChange={(e) => onQueryChange(e.target.value)}
					placeholder={searchPlaceholder}
					disabled={disabled}
					className='dual-list-search-input tw:w-full tw:border-0 tw:bg-transparent tw:text-[13.5px] tw:text-ink-0 tw:outline-none tw:placeholder:text-ink-3'
					aria-label={heading}
				/>
			</div>
			<div className='tw:flex-1 tw:overflow-y-auto'>
				{items.length === 0 ? (
					<div className='tw:flex tw:h-full tw:items-center tw:justify-center tw:text-center tw:text-[12.5px] tw:text-ink-3'>
						{emptyMessage}
					</div>
				) : (
					items.map((item) => {
						const isChecked = checked.has(item.value);
						return (
							<div
								key={item.value}
								role='option'
								aria-selected={isChecked}
								tabIndex={disabled ? undefined : 0}
								onClick={disabled ? undefined : () => onToggle(item.value)}
								onKeyDown={
									disabled
										? undefined
										: (e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												onToggle(item.value);
											}
										}
								}
								className={classnames(
									'tw:flex tw:items-center tw:gap-2 tw:px-3 tw:py-1.5 tw:text-[13.5px] tw:transition-colors tw:duration-100',
									disabled ? 'tw:cursor-not-allowed tw:opacity-60' : 'tw:cursor-pointer tw:hover:bg-surface-2',
									isChecked ? 'tw:bg-accent-soft tw:text-accent-ink' : 'tw:text-ink-1',
								)}
							>
								<input
									type='checkbox'
									checked={isChecked}
									readOnly
									tabIndex={-1}
									className='tw:pointer-events-none tw:h-4 tw:w-4 tw:rounded tw:border-border-strong tw:accent-accent'
								/>
								<span>{item.label}</span>
							</div>
						);
					})
				)}
			</div>
		</div>
	);
}

/**
 * Two-list membership + order picker (available ↔ active). `value` is the
 * ordered array of active option values;
 * the parent owns it and passes the next array back through `onChange`.
 * Search filters visibility only within each box — it never affects which
 * items `»`/`«` operate on, since those always act on full membership.
 */
export default function DualList({
	options,
	value,
	onChange,
	availableHeader,
	selectedHeader,
	searchPlaceholder = 'Buscar...',
	disabled = false,
}) {
	const [leftChecked, setLeftChecked] = useState(() => new Set());
	const [rightChecked, setRightChecked] = useState(() => new Set());
	const [leftQuery, setLeftQuery] = useState('');
	const [rightQuery, setRightQuery] = useState('');

	const optionsByValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);
	const availableItems = useMemo(() => options.filter((o) => !value.includes(o.value)), [options, value]);
	const activeItems = useMemo(
		() => value.map((v) => optionsByValue.get(v)).filter(Boolean),
		[value, optionsByValue],
	);

	const filteredAvailable = useMemo(
		() => availableItems.filter((o) => o.label.toLowerCase().includes(leftQuery.toLowerCase())),
		[availableItems, leftQuery],
	);
	const filteredActive = useMemo(
		() => activeItems.filter((o) => o.label.toLowerCase().includes(rightQuery.toLowerCase())),
		[activeItems, rightQuery],
	);

	const toggleLeft = (v) => {
		setLeftChecked((prev) => {
			const next = new Set(prev);
			next.has(v) ? next.delete(v) : next.add(v);
			return next;
		});
	};
	const toggleRight = (v) => {
		setRightChecked((prev) => {
			const next = new Set(prev);
			next.has(v) ? next.delete(v) : next.add(v);
			return next;
		});
	};

	const moveSelectedRight = () => {
		if (leftChecked.size === 0) return;
		const toMove = availableItems.filter((o) => leftChecked.has(o.value)).map((o) => o.value);
		onChange([...value, ...toMove]);
		setLeftChecked(new Set());
	};
	const moveAllRight = () => {
		onChange(options.map((o) => o.value));
		setLeftChecked(new Set());
	};
	const moveSelectedLeft = () => {
		if (rightChecked.size === 0) return;
		onChange(value.filter((v) => !rightChecked.has(v)));
		setRightChecked(new Set());
	};
	const moveAllLeft = () => {
		onChange([]);
		setRightChecked(new Set());
	};

	const singleCheckedRight = rightChecked.size === 1 ? [...rightChecked][0] : null;
	const canReorder = singleCheckedRight !== null;
	const moveUp = () => {
		if (!canReorder) return;
		const idx = value.indexOf(singleCheckedRight);
		if (idx <= 0) return;
		const next = [...value];
		[next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
		onChange(next);
	};
	const moveDown = () => {
		if (!canReorder) return;
		const idx = value.indexOf(singleCheckedRight);
		if (idx === -1 || idx >= value.length - 1) return;
		const next = [...value];
		[next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
		onChange(next);
	};

	const headClass = 'tw:text-center tw:text-[11px] tw:font-bold tw:uppercase tw:tracking-[0.06em] tw:text-ink-3';

	return (
		<div className='tw:space-y-3'>
			<div className='tw:hidden tw:grid-cols-[1fr_56px_1fr] tw:gap-3 tw:sm:grid'>
				<div className={headClass}>{availableHeader}</div>
				<div aria-hidden='true' />
				<div className={headClass}>{selectedHeader}</div>
			</div>
			<div className='tw:grid tw:grid-cols-1 tw:gap-3 tw:sm:grid-cols-[1fr_56px_1fr]'>
				<div className={classnames(headClass, 'tw:sm:hidden')}>{availableHeader}</div>
				<Box
					heading={availableHeader}
					query={leftQuery}
					onQueryChange={setLeftQuery}
					searchPlaceholder={searchPlaceholder}
					items={filteredAvailable}
					checked={leftChecked}
					onToggle={toggleLeft}
					disabled={disabled}
					emptyMessage={leftQuery ? 'Sin resultados' : 'Sin elementos disponibles'}
				/>

				<div className='tw:flex tw:flex-row tw:items-center tw:justify-center tw:gap-1.5 tw:sm:flex-col'>
					<CtrlButton label='Mover seleccionados a activas' onClick={moveSelectedRight} disabled={disabled || leftChecked.size === 0}>
						›
					</CtrlButton>
					<CtrlButton label='Mover todas a activas' onClick={moveAllRight} disabled={disabled || availableItems.length === 0}>
						»
					</CtrlButton>
					<CtrlButton label='Mover todas a disponibles' onClick={moveAllLeft} disabled={disabled || activeItems.length === 0}>
						«
					</CtrlButton>
					<CtrlButton label='Mover seleccionados a disponibles' onClick={moveSelectedLeft} disabled={disabled || rightChecked.size === 0}>
						‹
					</CtrlButton>
					<div className='tw:h-px tw:w-6 tw:bg-border tw:sm:h-6 tw:sm:w-px' />
					<CtrlButton label='Subir' onClick={moveUp} disabled={disabled || !canReorder}>
						▲
					</CtrlButton>
					<CtrlButton label='Bajar' onClick={moveDown} disabled={disabled || !canReorder}>
						▼
					</CtrlButton>
				</div>

				<div className={classnames(headClass, 'tw:sm:hidden')}>{selectedHeader}</div>
				<Box
					heading={selectedHeader}
					query={rightQuery}
					onQueryChange={setRightQuery}
					searchPlaceholder={searchPlaceholder}
					items={filteredActive}
					checked={rightChecked}
					onToggle={toggleRight}
					disabled={disabled}
					emptyMessage={rightQuery ? 'Sin resultados' : 'Sin elementos activos'}
				/>
			</div>
		</div>
	);
}

DualList.propTypes = {
	options: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
			label: PropTypes.node.isRequired,
		}),
	).isRequired,
	value: PropTypes.array.isRequired,
	onChange: PropTypes.func.isRequired,
	availableHeader: PropTypes.string.isRequired,
	selectedHeader: PropTypes.string.isRequired,
	searchPlaceholder: PropTypes.string,
	disabled: PropTypes.bool,
};
